/* eslint-disable no-nested-ternary */
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { CREATE_INVOICE } from '../../../graphql/invoices/mutation';
import {
  GET_ALL_INVOICES,
  USER_INVOICES,
} from '../../../graphql/invoices/query';
import { useNotification } from '../../../hooks/useNotification';
import { usePermissions } from '../../../hooks/usePermissions';
import { singleCampaignInfoAtom, userAtom } from '../../../states/atoms';
import BrandInvoiceForm from '../../common/BrandInvoiceForm';
import { Error } from '../../common/Error';
import FileUploader from '../../common/FileUploader';
import { SingleInvoice } from '../invoices/SingleInvoice';

export const SingleCampaignInvoicesTab = () => {
  const { id } = useParams();
  const [campaignId, influencerId, brandId]: [string, string, string] =
    useOutletContext();
  const campaignInfo = useRecoilValue(singleCampaignInfoAtom);
  const { t } = useTranslation('common');
  const isBrandMemo = useMemo(() => brandId === id, [brandId, id]);
  const [invoices, setInvoices] = useState<any>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { isInfluentia, isInfluencer } = usePermissions();
  const user = useRecoilValue(userAtom);
  useEffect(() => {
    setInvoices([]);
  }, [id]);

  const { refetch } = useQuery(USER_INVOICES, {
    skip: id !== 'influencers',
    variables: {
      campaignId,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setInvoices(data.invoicesUser.data);
    },
  });
  const { refetch: refetchBrand } = useQuery(GET_ALL_INVOICES, {
    skip: id === 'influencers',
    variables: {
      campaignId,
      brandId: isBrandMemo ? id : undefined,
      brand: isBrandMemo,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setInvoices(data.invoices.data);
    },
  });
  const {
    control,
    watch,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      currency: '€',
      billingDate: undefined,
      Price: undefined,
      paymentDate: undefined,
      userId: undefined,
      items: [],
      country: undefined,
    },
  });
  const fileUploaderRef = useRef<{
    resetInput: (reset: Boolean) => void;
  } | null>(null);

  const handleReset = () => {
    if (fileUploaderRef.current) {
      fileUploaderRef.current.resetInput(true);
    }
  };

  const [uploadInvoice, { loading }] = useMutation(CREATE_INVOICE);
  const { notifySaveSuccess, notifySaveError, notifyError } = useNotification();
  const onSubmit = (file?: File) => {
    uploadInvoice({
      variables: {
        file,
        campaignId,
        paymentDate: watch('paymentDate'),
        billingDate: watch('billingDate'),
        currency: watch('currency'),
        price: watch('Price'),
        userId: isInfluencer() ? user.id : watch('userId'),
        brandId: isBrandMemo ? id : undefined,
        items: watch('items'),
        country: watch('country'),
      },
      context: {
        headers: file
          ? {
              // 'content-type': 'multipart/form-data',
              'x-apollo-operation-name': 'CreateInvoice',
            }
          : {
              'x-apollo-operation-name': 'CreateInvoice',
            },
      },
      onCompleted: (res) => {
        if (res.createInvoice.code === 200) {
          notifySaveSuccess();
          refetch();
          handleReset();
          refetchBrand();
          setValue('items', []);
          reset();
          setSubmitted(true);
          setTimeout(() => {
            setSubmitted(false);
          }, 1000);
        } else {
          notifySaveError();
        }
      },
      onError: () => {
        notifySaveError();
      },
    });
  };
  const { isBrand } = usePermissions();

  useEffect(() => {
    const totalAmount = watch('items').reduce((acc: number, item: any) => {
      return acc + (item.amount ? parseFloat(item.amount) : 0);
    }, 0);
    setValue('Price', String(totalAmount || 0) as any);
  }, [JSON.stringify(watch('items'))]);

  return (
    <div className="flex flex-col gap-5 h-full">
      <div className="flex flex-col gap-5 h-full">
        {isBrandMemo &&
          !campaignInfo?.brand_holded_id_spain &&
          isInfluentia() && (
            <Error error={t('singleCampaign.noBrandHoldedIdSpain')} />
          )}
        {isBrandMemo &&
          !campaignInfo?.brand_holded_id_andorra &&
          isInfluentia() && (
            <Error error={t('singleCampaign.noBrandHoldedIdAndorra')} />
          )}
        {/* {!isBrandMemo && isInfluentia() && (
          <>
            {campaignInfo?.influencers
              .filter((i) => !i.holded_id_andorra || !i.holded_id_spain)
              .map((influencer: any) => (
                <Error
                  key={influencer.id}
                  error={t('singleCampaign.noInfluencerHoldedId', {
                    influencerName: influencer.name,
                  })}
                />
              ))}
          </>
        )} */}
        <div className="flex flex-col size-full gap-5">
          {invoices && invoices.length > 0 ? (
            isBrandMemo ? (
              <div
                className={`${
                  isInfluentia()
                    ? 'grid 2xl:grid-cols-2 xl:grid-cols-1 lg:grid-cols-1 md:grid-cols-1 grid-cols-1 sm:grid-cols-2'
                    : 'flex flex-col'
                } gap-2.5`}
              >
                {invoices.map((invoice: any) => (
                  <SingleInvoice
                    key={invoice.id}
                    invoice={invoice}
                    refetch={refetchBrand}
                    isBrandInvoice
                    synced={!!invoice.holdedDocumentId}
                  />
                ))}
              </div>
            ) : (
              invoices.map(
                (userInvoice: any) =>
                  userInvoice &&
                  userInvoice.user &&
                  userInvoice.invoices &&
                  userInvoice.invoices.length > 0 && (
                    <div
                      key={userInvoice.user.id}
                      className={`flex flex-col gap-2.5`}
                    >
                      <span
                        className={`text-white italic ${isInfluencer() && 'hidden'}`}
                      >
                        {userInvoice.user.name}
                      </span>
                      {isInfluentia() && (
                        <>
                          {!userInvoice.user?.holdedIdSpain && (
                            <Error
                              key={`${userInvoice.user.id}spain`}
                              error={t(
                                'singleCampaign.noInfluencerHoldedIdSpain',
                                {
                                  influencerName: userInvoice.user.name,
                                },
                              )}
                            />
                          )}
                          {!userInvoice.user?.holdedIdAndorra && (
                            <Error
                              key={`${userInvoice.user.id}andorra`}
                              error={t(
                                'singleCampaign.noInfluencerHoldedIdAndorra',
                                {
                                  influencerName: userInvoice.user.name,
                                },
                              )}
                            />
                          )}
                        </>
                      )}
                      <div
                        className={`${
                          isInfluentia()
                            ? 'grid 2xl:grid-cols-2 xl:grid-cols-1 lg:grid-cols-1 md:grid-cols-1 grid-cols-1 sm:grid-cols-2'
                            : 'flex flex-col'
                        } gap-2.5`}
                      >
                        {userInvoice.invoices.map(
                          (invoice: any) =>
                            invoice &&
                            invoice.id && (
                              <SingleInvoice
                                key={invoice.id}
                                invoice={invoice}
                                refetch={refetch}
                                synced={!!invoice.holdedDocumentId}
                              />
                            ),
                        )}
                      </div>
                    </div>
                  ),
              )
            )
          ) : (
            <>
              {(!invoices || (invoices && invoices.length === 0)) &&
                (isBrandMemo || isBrand()) && (
                  <span className="text-white italic">
                    {t('singleCampaign.noInvoicesBrand')}
                  </span>
                )}
              {(!invoices || (invoices && invoices.length === 0)) &&
                !isBrandMemo &&
                isInfluentia() && (
                  <span className="text-white italic">
                    {t('singleCampaign.noInvoices')}
                  </span>
                )}
            </>
          )}
          {(isInfluentia() || isInfluencer()) && !isBrandMemo && (
            <FileUploader
              fileType="Invoice"
              handleSubmit={handleSubmit}
              resetOnSubmit={true}
              ref={fileUploaderRef}
              submitFile={onSubmit}
              loading={loading}
              control={control}
              currentCurrency={watch('currency')}
              errors={errors}
              influencers={!isBrandMemo ? campaignInfo?.influencers : undefined}
            />
          )}
          {isInfluentia() && isBrandMemo && (
            <BrandInvoiceForm
              handleSubmit={() => handleSubmit(() => onSubmit())()}
              loading={loading}
              control={control}
              currentCurrency={watch('currency')}
              errors={errors}
              onCancel={() => {
                reset();
              }}
              submitted={submitted}
            />
          )}
        </div>

        <div>
          {isInfluencer() && (
            <div className="flex flex-col">
              <span className="uppercase text-white">
                {t('singleCampaign.facturationInfo')}
              </span>
              <span className="text-white">
                {t('singleCampaign.facturationElection')}
              </span>
              <div className="bg-dark-blue p-2.5 rounded-10 flex flex-col">
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.companyName')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.americanCompany')}
                </span>
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.fiscalAddress')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.americanCompanyAddress')}
                </span>
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.tin')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.americanCompanyTin')}
                </span>
              </div>
              <span className="text-white">
                {t('singleCampaign.facturationElection1')}
              </span>
              <div className="bg-dark-blue p-2.5 rounded-10 flex flex-col">
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.companyName')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.spanishCompany')}
                </span>
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.fiscalAddress')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.spanishCompanyAddress')}
                </span>
                <span className="text-[10px] text-influentia-light-blue font-medium">
                  {t('singleCampaign.cif')}
                </span>
                <span className="text-white">
                  {t('singleCampaign.spanishCompanyCif')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
